import { consumerRoot } from 'src/config'
import Model from 'src/models/model'
import { CURRENTLY_PROCESSING_GUARDS, DEFAULT_SHARED_DOMAIN_SITE_GUARDS, redirectToPreferredOrLastSiteAtStart } from 'src/modules/consumer/guards/sharedDomainSiteSwitching'
import DynamicConsumerLayout from 'src/modules/consumer/layouts/DynamicConsumerLayout'
import { prependWithConsumerRoot } from 'src/router/utils'

/**
 * @typedef {import('vue-router').RouteRecordRaw} RouteRecordRaw
 * @typedef {import('vue-router').NavigationGuard} NavigationGuard
 */

/**
 * Builds the parent route object that holds all consumer routes as child routes.
 * @returns {RouteRecordRaw}
 */
export function buildConsumerRootRoute (options) {
  const { namespace, siteId, siteRootPath, isHostnameSharedAcrossSites = false, isWwwSubdomain = false, isNakedDomain = false } = options

  const rootRoute = {
    name: `${namespace}:root-parent`,
    path: siteRootPath === consumerRoot ? consumerRoot : prependWithConsumerRoot(siteRootPath),
    component: DynamicConsumerLayout,
    // Child routes inherit their parent route's meta fields but can override them.
    // `meta.layout` is used by DynamicConsumerLayout to render the appropriate layout component.
    // `meta.requiredSiteId` is used by the `ensureSwitchedToSiteRequiredByRoute` guard.
    meta: { layout: 'ConsumerLayout', requiredSiteId: siteId },
    children: buildConsumerChildRoutes(namespace)
  }

  _.each(rootRoute.children, route => {
    const beforeEnterGuards = []
    const isDefaultRootOrHomePageRoute = route.name === 'consumer:root' || route.name === 'consumer:home-page'
    // If the circuit has multiple sites, whether across subpath or subdomains,
    // we only care about checking for and executing any necessary redirects out
    // of the primary site's initial route.
    const shouldMaybeRedirectToPreferredOrLastVisitedSite = isDefaultRootOrHomePageRoute && (
      isHostnameSharedAcrossSites || isWwwSubdomain || isNakedDomain
    )
    if (shouldMaybeRedirectToPreferredOrLastVisitedSite) {
      beforeEnterGuards.push(redirectToPreferredOrLastSiteAtStart)
    }

    if (isHostnameSharedAcrossSites) {
      beforeEnterGuards.push(DEFAULT_SHARED_DOMAIN_SITE_GUARDS)
    }

    beforeEnterGuards.push(route.beforeEnter)
    route.beforeEnter = _(beforeEnterGuards).flattenDeep().compact().value()
  })

  return rootRoute
}

/**
 * Builds the child route objects for a consumer root parent route. Note the
 * `meta.layout` field can change which layout component is used for each route.
 * When `meta.layout` missing, it defaults to the parent route's layout (ConsumerLayout).
 *
 * We use a function to support dynamic generation and to ensure each parent route
 * has its own child routes that do not share references.
 * @returns {RouteRecordRaw[]}
 */
export function buildConsumerChildRoutes (namespace = 'consumer') {
  const isForDefaultRootPath = namespace === 'consumer'

  // The following route guards need to reference the given namespace so we define them
  // locally. We use named rather than arrow functions to make stack traces clearer.

  /** @type {NavigationGuard} */
  async function redirectToHomePageOr404 (to, _from, next) {
    await Model.ensureBooted()
    const homePage = Model.configuration.publishedPages.homePage

    if (homePage?.slug) {
      next({
        name: `${namespace}:home-page`,
        params: { slug: homePage.slug },
        query: { ...to.query },
        hash: to.hash
      })
    } else {
      next({ name: 'statics:404', query: { ...to.query } })
    }
  }

  /** @type {NavigationGuard} */
  async function redirectToHomePageOr404IfPageNotFound (to, _from, next) {
    await Model.ensureBooted()
    const slug = to.params?.slug
    const match = Model.configuration.publishedPages.find(page => page.visible && page.slug === slug)

    if (match) {
      next()
    } else {
      // No match, head home!
      const homePage = Model.configuration.publishedPages.homePage

      if (homePage?.slug) {
        next({
          name: `${namespace}:home-page`,
          params: { slug: homePage.slug },
          query: { ...to.query },
          hash: to.hash
        })
      } else {
        next({ name: 'statics:404', query: { ...to.query }, hash: to.hash })
      }
    }
  }

  /** @type {NavigationGuard} */
  async function redirectToExploreMoviesIfNoMoviesPage (_to, _from, next) {
    await Model.ensureBooted()

    const match = Model.configuration.publishedPages.find(page => {
      return page.visible && page.slug === 'movies'
    })

    if (!match) {
      next({ name: `${namespace}:explore-movies` })
    } else {
      next()
    }
  }

  const childRoutes = [
    // root route
    {
      name: `${namespace}:root`,
      path: '',
      beforeEnter: redirectToHomePageOr404,
      // This component isn't actually used as we should always be redirected
      // by the beforeEnter guard. It's just here to avoid a Vue Router warning.
      component: () => import('src/modules/consumer/pages/CustomPage')
    },

    // AccountLayout routes
    {
      name: `${namespace}:my-membership`,
      path: 'my-membership',
      meta: { layout: 'AccountLayout' },
      component: () => import('src/modules/consumer/pages/MyMembershipPage')
    },
    {
      name: `${namespace}:manage-members`,
      path: 'manage-members',
      meta: { layout: 'AccountLayout' },
      component: () => import('src/modules/consumer/pages/ManageMembersPage')
    },
    {
      name: `${namespace}:manage-friends`,
      path: 'manage-friends',
      meta: { layout: 'AccountLayout' },
      component: () => import('src/modules/consumer/pages/ManageFriendsPage')
    },
    {
      name: `${namespace}:account-information`,
      path: 'account-information',
      meta: { title: 'Account Information', layout: 'AccountLayout' },
      component: () => import('src/modules/consumer/pages/AccountInformationPage')
    },
    {
      name: `${namespace}:orders`,
      path: 'orders',
      meta: { layout: 'AccountLayout' },
      component: () => import('src/modules/consumer/pages/UserOrders')
    },
    {
      name: `${namespace}:my-movies`,
      path: 'my-movies',
      meta: { layout: 'AccountLayout' },
      component: () => import('src/modules/consumer/pages/MyMovies')
    },
    {
      name: `${namespace}:manage-marketing-preferences`,
      path: 'marketing-preferences',
      meta: { layout: 'AccountLayout' },
      component: () => import('src/modules/consumer/pages/ManageMarketingPreferences')
    },
    {
      name: `${namespace}:update-payment-method`,
      path: 'update-payment-method/:token?',
      meta: { layout: 'AccountLayout' },
      component: () => import('src/modules/consumer/pages/UpdatePaymentMethod')
    },

    // PlainLayout routes

    {
      name: `${namespace}:subscriptions-unsubscribe`,
      path: 'subscriptions/:token/:subscription_id/unsubscribe',
      meta: { layout: 'PlainLayout' },
      component: () => import('src/modules/consumer/pages/UpdateUserMarketingSubscription')
    },
    {
      name: `${namespace}:subscriptions`,
      path: 'subscriptions/:token?',
      meta: { layout: 'PlainLayout' },
      component: () => import('src/modules/consumer/pages/UpdateUserMarketingSubscriptions')
    },
    {
      name: `${namespace}:visit-review`,
      path: 'visit-review/:token/:rating?',
      meta: { layout: 'PlainLayout' },
      component: () => import('src/modules/consumer/pages/VisitReview')
    },
    {
      name: `${namespace}:receipt`,
      path: 'receipt',
      meta: { layout: 'PlainLayout' },
      component: () => import('src/modules/consumer/pages/Receipt')
    },

    // ConsumerLayout routes

    {
      name: `${namespace}:movie`,
      path: 'movie/:slug',
      meta: { canonical: true },
      component: () => import('src/modules/consumer/pages/MoviePage')
    },
    {
      name: `${namespace}:activity`,
      path: 'activity/:slug',
      component: () => import('src/modules/consumer/pages/ActivityPage')
    },
    {
      name: `${namespace}:card`,
      path: 'card',
      component: () => import('src/modules/consumer/pages/Card')
    },
    {
      name: `${namespace}:news`,
      path: 'news',
      component: () => import('src/modules/consumer/pages/News')
    },
    {
      name: `${namespace}:explore-movies`,
      path: 'explore-movies',
      component: () => import('src/modules/consumer/pages/ExploreMovies')
    },
    {
      name: `${namespace}:select-location`,
      path: 'select-location',
      component: () => import('src/modules/consumer/pages/LocationSelect')
    },
    {
      name: `${namespace}:movies`,
      path: 'movies',
      beforeEnter: redirectToExploreMoviesIfNoMoviesPage
    },

    {
      name: `${namespace}:page-redirect`,
      path: 'page/:slug',
      redirect: { name: `${namespace}:custom-page`, params: { slug: ':slug' } }
    },

    // Even though mostly functionally equivalent, the following two routes must be
    // added as different routes in the following order. Previously the `custom-page` route
    // was handling a lot: custom pages, home page, and 404s. The problem is that vue-router
    // can skip beforeEnter guards when redirecting to the same route with only different
    // params/query, e.g. a redirect chain of `custom-page` -> `custom-page` -> `custom-page` with
    // changed params would skip subsequent guards. We need the guards to always fire, so we
    // define a separate named route for the home page. The order matters because unfortunately
    // we need to set the same path to match, and the first match wins.
    {
      name: `${namespace}:custom-page`,
      path: ':slug',
      meta: { canonical: true },
      component: () => import('src/modules/consumer/pages/CustomPage'),
      beforeEnter: [
        // Sometimes we need to accurately detect if the user is on the homepage specifically.
        // So even though functionally equivalent, we force the router to use the ":home-page"
        // route instead of this conflicting ":custom-page" route for the homepage.
        async function redirectToHomePageRouteIfHomePage (to, _from, next) {
          if (CURRENTLY_PROCESSING_GUARDS.has('redirectToHomePageRouteIfHomePage')) {
            next()
            return
          }
          CURRENTLY_PROCESSING_GUARDS.add('redirectToHomePageRouteIfHomePage')

          await Model.ensureBooted()
          const homePage = Model.configuration.publishedPages.homePage
          if (homePage?.slug && to.params.slug === homePage.slug && to.name !== `${namespace}:custom-page`) {
            next({ name: `${namespace}:home-page`, params: { slug: homePage.slug } })
          } else {
            next()
          }
        },
        redirectToHomePageOr404IfPageNotFound
      ]
    },
    {
      name: `${namespace}:home-page`,
      path: ':slug',
      meta: { canonical: true },
      component: () => import('src/modules/consumer/pages/CustomPage')
    },

    // The following redirects are special in that we want them to always work at the top-level '/'
    // regardless of what the app mode root is (e.g. '/' vs '/consumer/'), so their path strings are
    // set with a leading slash ('/'). This means they will be added as absolute paths at the top-level
    // instead of being nested under their parent route's path. This is desirable when adding these
    // routes on the default top-level root route but not when adding under shared domain sites'
    // root paths (e.g. '/some-site/...'). So we only include the leading slash when building for
    // the default root path.
    {
      path: isForDefaultRootPath ? '/u/:uid' : 'u/:uid',
      redirect: { name: `${namespace}:root` }
    },
    {
      path: isForDefaultRootPath ? '/g/:uid' : 'g/:uid',
      redirect: { name: `${namespace}:root` }
    },

    // The catch-all route can be added only once at the top-level root since it
    // can match any subpaths.
    ...(isForDefaultRootPath
      ? [{
          name: `${namespace}:not-found`,
          path: '/:pathMatch(.*)*',
          redirect: to => {
            return {
              name: `${namespace}:root`,
              query: to.query,
              hash: to.hash
            }
          }
        }]
      : [])
  ]

  return childRoutes
}
